import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { apiClient } from '../../apiClient';
import { auth } from '../../firebase';

const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Date';
  return date.toLocaleString('ja-JP', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit', 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

const OperationLogs = () => {
  const [logs, setLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [lastDocId, setLastDocId] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [searchInputs, setSearchInputs] = useState({
    admin_email: '',
    operation_type: '',
    target_type: '',
    target_id: '',
    start_date: '',
    end_date: ''
  });

  const [activeFilters, setActiveFilters] = useState({});

  const fetchLogs = useCallback(async (filters, isLoadMore = false) => {
    if (isLoadMore ? loadingMore : loading) return;
    isLoadMore ? setLoadingMore(true) : setLoading(true);

    try {
      const params = { ...filters };
      if (isLoadMore && lastDocId) {
        params.lastDocId = lastDocId;
      }

      const queryString = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/v1/admin/operation-logs?${queryString}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch operation logs');
      }
      
      setLogs(prev => isLoadMore ? [...prev, ...data] : data);
      
      if (data.length < 50) {
        setHasMore(false);
      } else {
        setLastDocId(data[data.length - 1].id);
        setHasMore(true);
      }
    } catch (error) {
      console.error('操作履歴の取得中にエラーが発生しました:', error);
      setHasMore(false);
    } finally {
      isLoadMore ? setLoadingMore(false) : setLoading(false);
    }
  }, [lastDocId, loading, loadingMore]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        fetchLogs({}, false);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleSearchChange = (e) => {
    setSearchInputs({ ...searchInputs, [e.target.name]: e.target.value });
  };

  const handleSearch = () => {
    const cleanFilters = Object.fromEntries(
      Object.entries(searchInputs).filter(([_, value]) => value !== '')
    );
    setActiveFilters(cleanFilters);
    setLastDocId(null);
    setLogs([]);
    fetchLogs(cleanFilters, false);
  };

  const handleReset = () => {
    const emptyInputs = {
      admin_email: '',
      operation_type: '',
      target_type: '',
      target_id: '',
      start_date: '',
      end_date: ''
    };
    setSearchInputs(emptyInputs);
    setActiveFilters({});
    setLastDocId(null);
    setLogs([]);
    fetchLogs({}, false);
  };
  
  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
        fetchLogs(activeFilters, true);
    }
  };

  const toggleLogDetails = (log) => {
    setSelectedLog(prev => (prev && prev.id === log.id) ? null : log);
  };

  const getOperationTypeName = (type) => {
    const types = {
      tenant_suspend: '利用停止',
      tenant_resume: '利用再開',
      invoice_lock: '請求書発行停止',
      invoice_unlock: '請求書発行再開',
      user_disable: 'アカウント無効化',
      user_enable: 'アカウント有効化',
      force_logout: '強制ログアウト',
      invoice_resend: '請求書再送信',
      trial_start: 'トライアル開始',
      trial_extend: 'トライアル延長',
      trial_end_schedule: 'トライアル終了予約',
      stripe_settings_updated: 'Stripe共通設定変更',
      maintenance_enabled: 'メンテナンスモード有効化',
      maintenance_disabled: 'メンテナンスモード無効化',
      dashboard_refresh: 'ダッシュボード統計更新',
      trial_settings_updated: 'トライアル受付設定変更',
      refund_execute: '返金実行',
      credit_execute: '個別返金予約',
      system_user_create: 'システムユーザー追加',
      system_user_update: 'システムユーザー編集',
      system_user_disable: 'システムユーザー無効化',
      system_user_enable: 'システムユーザー再有効化',
      bulk_refund_create: '一括返金作成',
      bulk_refund_execute: '一括返金実行',
      lp_content_update: 'LPコンテンツ更新',
      announcement_create: 'お知らせ作成',
      announcement_update: 'お知らせ更新',
      announcement_delete: 'お知らせ削除',
      status_update: '障害・メンテナンス管理更新',
      faq_create: 'FAQ作成',
      faq_update: 'FAQ更新',
      faq_delete: 'FAQ削除',
      release_note_create: 'リリースノート作成',
      release_note_update: 'リリースノート更新',
      release_note_delete: 'リリースノート削除'
    };
    return types[type] || type;
  };

  const getTargetTypeName = (type) => {
    const types = {
      tenant: '企業',
      user: 'ユーザー',
      system_user: 'システムユーザー',
      invoice: '請求書',
      system_settings: 'システム設定',
      dashboard: 'ダッシュボード',
      bulk_refund: '一括返金',
      lp_content: 'LPコンテンツ',
      announcement: 'お知らせ',
      status: '障害・ステータス',
      faq: 'FAQ',
      release_note: 'リリースノート'
    };
    return types[type] || type;
  };

  const LogRow = ({ log }) => (
    <>
      <tr onClick={() => toggleLogDetails(log)} className="cursor-pointer hover:bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatDate(log.operated_at)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.admin_email}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{getOperationTypeName(log.operation_type)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.target_name || log.target_id}</td>
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <button className="text-indigo-600 hover:text-indigo-900">
            {selectedLog && selectedLog.id === log.id ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </button>
        </td>
      </tr>
      {selectedLog && selectedLog.id === log.id && (
        <tr>
          <td colSpan="5" className="px-2 py-2 md:px-4 md:py-4 bg-gray-50">
            <LogDetails log={selectedLog} />
          </td>
        </tr>
      )}
    </>
  );

  const LogDetails = ({ log }) => (
    <div className="bg-white p-4 rounded-lg shadow-inner text-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-bold border-b pb-2 mb-3">基本情報</h4>
          <div className="space-y-2">
            <div><strong>操作日時:</strong> <p className="text-gray-700">{formatDate(log.operated_at)}</p></div>
            <div><strong>実行者ID:</strong> <p className="text-gray-700">{log.admin_user_id}</p></div>
            <div><strong>実行者メールアドレス:</strong> <p className="text-gray-700">{log.admin_email}</p></div>
            <div><strong>操作種別:</strong> <p className="text-gray-700">{getOperationTypeName(log.operation_type)} ({log.operation_type})</p></div>
          </div>
        </div>
        <div>
          <h4 className="font-bold border-b pb-2 mb-3">対象情報</h4>
          <div className="space-y-2">
            <div><strong>対象種別:</strong> <p className="text-gray-700">{getTargetTypeName(log.target_type)}</p></div>
            <div><strong>対象ID:</strong> <p className="text-gray-700">{log.target_id}</p></div>
            <div><strong>対象名:</strong> <p className="text-gray-700">{log.target_name}</p></div>
            {log.reason && <div><strong>理由:</strong> <p className="text-gray-700 bg-yellow-50 p-2 rounded border border-yellow-100">{log.reason}</p></div>}
          </div>
        </div>
        <div className="col-span-full">
          <h4 className="font-bold border-b pb-2 mb-3">変更内容</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="font-semibold text-gray-600 mb-1">変更前</p>
              <pre className="bg-gray-100 p-3 rounded overflow-auto max-h-60 text-xs">
                {JSON.stringify(log.before_value, null, 2)}
              </pre>
            </div>
            <div>
              <p className="font-semibold text-gray-600 mb-1">変更後</p>
              <pre className="bg-gray-100 p-3 rounded overflow-auto max-h-60 text-xs">
                {JSON.stringify(log.after_value, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <h2 className="text-2xl font-bold mb-6">操作履歴（監査ログ）</h2>
      
      {/* 検索フォーム */}
      <div className="p-4 bg-gray-50 rounded-lg mb-6 shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-600">実行者メールアドレス</label>
            <input type="text" name="admin_email" placeholder="admin@example.com" value={searchInputs.admin_email} onChange={handleSearchChange} className="w-full p-2 border rounded shadow-sm"/>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-600">操作種別</label>
            <select name="operation_type" value={searchInputs.operation_type} onChange={handleSearchChange} className="w-full p-2 border rounded shadow-sm bg-white">
              <option value="">全ての操作</option>
              <option value="tenant_suspend">利用停止</option>
              <option value="tenant_resume">利用再開</option>
              <option value="invoice_lock">請求書発行停止</option>
              <option value="invoice_unlock">請求書発行再開</option>
              <option value="user_disable">アカウント無効化</option>
              <option value="user_enable">アカウント有効化</option>
              <option value="force_logout">強制ログアウト</option>
              <option value="invoice_resend">請求書再送信</option>
              <option value="trial_start">トライアル開始</option>
              <option value="trial_extend">トライアル延長</option>
              <option value="trial_end_schedule">トライアル終了予約</option>
              <option value="stripe_settings_updated">Stripe共通設定変更</option>
              <option value="maintenance_enabled">メンテナンスモード有効化</option>
              <option value="maintenance_disabled">メンテナンスモード無効化</option>
              <option value="dashboard_refresh">ダッシュボード統計更新</option>
              <option value="trial_settings_updated">トライアル受付設定変更</option>
              <option value="refund_execute">返金実行</option>
              <option value="credit_execute">個別返金予約</option>
              <option value="system_user_create">システムユーザー追加</option>
              <option value="system_user_update">システムユーザー編集</option>
              <option value="system_user_disable">システムユーザー無効化</option>
              <option value="system_user_enable">システムユーザー再有効化</option>
              <option value="bulk_refund_create">一括返金作成</option>
              <option value="bulk_refund_execute">一括返金実行</option>
              <option value="lp_content_update">LPコンテンツ更新</option>
              <option value="announcement_create">お知らせ作成</option>
              <option value="announcement_update">お知らせ更新</option>
              <option value="announcement_delete">お知らせ削除</option>
              <option value="status_update">障害・メンテナンス管理更新</option>
              <option value="faq_create">FAQ作成</option>
              <option value="faq_update">FAQ更新</option>
              <option value="faq_delete">FAQ削除</option>
              <option value="release_note_create">リリースノート作成</option>
              <option value="release_note_update">リリースノート更新</option>
              <option value="release_note_delete">リリースノート削除</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-600">対象種別</label>
            <select name="target_type" value={searchInputs.target_type} onChange={handleSearchChange} className="w-full p-2 border rounded shadow-sm bg-white">
              <option value="">全ての対象</option>
              <option value="tenant">企業</option>
              <option value="user">ユーザー</option>
              <option value="system_user">システムユーザー</option>
              <option value="invoice">請求書</option>
              <option value="system_settings">システム設定</option>
              <option value="dashboard">ダッシュボード</option>
              <option value="bulk_refund">一括返金</option>
              <option value="lp_content">LPコンテンツ</option>
              <option value="announcement">お知らせ</option>
              <option value="status">障害・ステータス</option>
              <option value="faq">FAQ</option>
              <option value="release_note">リリースノート</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-600">対象ID</label>
            <input type="text" name="target_id" placeholder="ID" value={searchInputs.target_id} onChange={handleSearchChange} className="w-full p-2 border rounded shadow-sm"/>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-600">開始日</label>
            <input type="date" name="start_date" value={searchInputs.start_date} onChange={handleSearchChange} className="w-full p-2 border rounded shadow-sm"/>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-600">終了日</label>
            <input type="date" name="end_date" value={searchInputs.end_date} onChange={handleSearchChange} className="w-full p-2 border rounded shadow-sm"/>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
            <button onClick={handleSearch} className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 shadow-sm disabled:bg-blue-400" disabled={loading}>検索</button>
            <button onClick={handleReset} className="px-4 py-2 bg-gray-300 text-gray-800 font-semibold rounded-md hover:bg-gray-400 shadow-sm">リセット</button>
        </div>
      </div>
      
      {/* 実行結果 */}
      {(loading && logs.length === 0) ? <p className="text-center py-4">読み込み中...</p> : logs.length === 0 ? <p className="text-center py-4 text-gray-500">操作履歴が見つかりません。</p> : (
        <>
          <div className="overflow-x-auto shadow-md sm:rounded-lg border">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作日時</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">実行者</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作種別</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">対象名</th>
                  <th className="relative px-6 py-3"><span className="sr-only">詳細</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map(log => <LogRow key={log.id} log={log} />)}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className="mt-6 text-center">
              <button onClick={handleLoadMore} disabled={loadingMore} className="px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400">
                {loadingMore ? '読み込み中...' : '次の50件を読み込む'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OperationLogs;
